/** Phase 2B — Campaigns Kanban view (drag-and-drop via @dnd-kit).
 *
 * Reads the authoritative state machine from `GET /api/v1/campaigns/transitions`
 * (never duplicated client-side). Each card exposes its current legal moves;
 * during drag, illegal drop targets are visually disabled.
 *
 * State → endpoint mapping (only legal moves are wired):
 *   draft     → in_review   POST /submit-review
 *   scheduled → active      POST /resume
 *   active    → paused      POST /pause
 *   active    → completed   POST /complete
 *   paused    → active      POST /resume
 *   paused    → completed   POST /complete
 *   completed → archived    POST /archive
 *
 * Anything else is blocked at drop time. The `approved` column is rendered
 * with an "auto-forwarded" label (ADR-0015) — dropping into it is blocked.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable, closestCenter,
} from "@dnd-kit/core";
import { api } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { toast } from "sonner";

const COLUMNS = [
  { key: "draft",     label: "Draft" },
  { key: "in_review", label: "In review" },
  { key: "approved",  label: "Approved (auto-forwarded)",
    labelDe: "Freigegeben (automatisch weitergeleitet)",
    autoForwarded: true },
  { key: "scheduled", label: "Scheduled" },
  { key: "active",    label: "Active" },
  { key: "paused",    label: "Paused" },
  { key: "completed", label: "Completed" },
  { key: "rejected",  label: "Rejected" },
  { key: "archived",  label: "Archived" },
];

// Map (from → to) → POST endpoint suffix.
const TRANSITION_ROUTE = {
  "draft|in_review": "submit-review",
  "scheduled|active": "resume",
  "active|paused": "pause",
  "paused|active": "resume",
  "active|completed": "complete",
  "paused|completed": "complete",
  "completed|archived": "archive",
};

const STATUS_VARIANTS = {
  draft: "secondary", in_review: "secondary",
  approved: "default", scheduled: "default", active: "default",
  paused: "outline", completed: "outline",
  rejected: "destructive", archived: "outline",
};

function platformIcon(p) {
  return ({ meta: "f", google: "G", google_search: "Gs", linkedin: "in",
            tiktok: "tk", youtube: "yt" })[p] || p.slice(0, 2);
}

export default function CampaignsKanban({ items, onChanged, locale = "en" }) {
  const [transitions, setTransitions] = useState({ allowed_transitions: {}, all_states: [] });
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/campaigns/transitions")
      .then((r) => setTransitions(r.data))
      .catch(() => {});
  }, []);

  const byStatus = useMemo(() => {
    const o = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    for (const c of items || []) {
      if (o[c.status]) o[c.status].push(c);
    }
    return o;
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const legalNext = (status) =>
    transitions.allowed_transitions[status] || [];

  const canDropOn = (campaign, targetStatus) => {
    if (!campaign) return false;
    if (campaign.status === targetStatus) return false;
    // approved is rendered but auto-forwarded — manual drop blocked
    if (targetStatus === "approved") return false;
    if (!(legalNext(campaign.status) || []).includes(targetStatus)) return false;
    return `${campaign.status}|${targetStatus}` in TRANSITION_ROUTE;
  };

  const onDragEnd = async (e) => {
    setActive(null);
    const dragged = e?.active?.data?.current;
    const dst = e?.over?.id;
    if (!dragged || !dst) return;
    if (!canDropOn(dragged, dst)) {
      const reason = dst === "approved"
        ? "The 'approved' state is set automatically once the publish approval is granted."
        : dragged.approval_status === "pending"
          ? "Approval required — open the campaign to review."
          : "Not a legal transition from the current state.";
      toast.error("Move blocked", { description: reason });
      return;
    }
    const suffix = TRANSITION_ROUTE[`${dragged.status}|${dst}`];
    try {
      await api.post(`/campaigns/${dragged.id}/${suffix}`,
                     undefined, { skipPermissionToast: true });
      toast.success(`Moved to ${dst.replace("_", " ")}`);
      onChanged?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        toast.error("You don't have permission to perform this transition.",
                    { description: d?.required ? `Required: ${d.required}` : undefined });
      } else if (d?.error === "illegal_transition") {
        toast.error("Illegal transition", { description: `From ${d.from} → ${d.to}` });
      } else {
        toast.error("Transition failed");
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActive(e.active.data.current)}
      onDragCancel={() => setActive(null)}
      onDragEnd={onDragEnd}
    >
      <div data-testid="kanban-board" className="flex gap-3 overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const target = active ? canDropOn(active, col.key) : null;
          return (
            <Column
              key={col.key}
              col={col}
              items={byStatus[col.key]}
              dimmed={active && target === false}
              highlighted={active && target === true}
              locale={locale}
            />
          );
        })}
      </div>
      <DragOverlay>
        {active ? <KanbanCard campaign={active} dragging /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function Column({ col, items, dimmed, highlighted, locale }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  const label = locale === "de" && col.labelDe ? col.labelDe : col.label;
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-col-${col.key}`}
      className={[
        "shrink-0 w-72 rounded-lg border bg-muted/20 p-3 transition-opacity",
        dimmed ? "opacity-40" : "opacity-100",
        highlighted || isOver ? "ring-2 ring-primary" : "",
        col.autoForwarded ? "border-dashed" : "",
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
          {label}
        </div>
        <Badge variant="outline" data-testid={`kanban-count-${col.key}`}>{items.length}</Badge>
      </div>
      <div className="space-y-2 min-h-[40px]">
        {items.length === 0 && (
          <div className={[
            "text-xs text-muted-foreground italic px-2 py-3 text-center",
            col.autoForwarded ? "" : "opacity-60",
          ].join(" ")}>
            {col.autoForwarded
              ? (locale === "de" ? "wird automatisch weitergeleitet" : "auto-forwarded")
              : "empty"}
          </div>
        )}
        {items.map((c) => <DraggableCard key={c.id} campaign={c} />)}
      </div>
    </div>
  );
}

function DraggableCard({ campaign }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: campaign.id,
    data: campaign,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`kanban-card-${campaign.id}`}
      className={isDragging ? "opacity-0" : ""}
    >
      <KanbanCard campaign={campaign} />
    </div>
  );
}

function KanbanCard({ campaign: c, dragging }) {
  return (
    <Card className={[
      "p-3 cursor-grab active:cursor-grabbing select-none space-y-2",
      dragging ? "shadow-lg rotate-1" : "hover:bg-accent/40",
    ].join(" ")}>
      <div className="flex items-start justify-between gap-2">
        <Link
          to={`/app/campaigns/${c.id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-sm font-medium leading-tight hover:underline"
          data-testid={`kanban-card-link-${c.id}`}
        >
          {c.name}
        </Link>
        <Badge variant={STATUS_VARIANTS[c.status] || "outline"} className="text-[10px]">
          {c.campaign_type}
        </Badge>
      </div>
      <div className="flex flex-wrap items-center gap-1 text-[10px]">
        {(c.platforms || []).map((p) => (
          <span key={p} className="font-mono px-1.5 py-0.5 rounded border bg-background">
            {platformIcon(p)}
          </span>
        ))}
        {c.mocked_publish && (
          <Badge variant="outline" className="text-[10px]">mocked</Badge>
        )}
        {c.pending_approval_id && (
          <Badge variant="secondary" className="text-[10px]">pending</Badge>
        )}
      </div>
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span>{c.budget?.amount ? `€${c.budget.amount}/${c.budget.type?.slice(0,1) || "d"}` : "—"}</span>
        <span>{c.updated_at ? new Date(c.updated_at).toLocaleDateString() : ""}</span>
      </div>
    </Card>
  );
}
